import React, { useState } from "react";
import { FieldPath, FieldValues, Controller, Control } from "react-hook-form";

import { cn } from "@/lib/utils";
import FluidImage from "@/components/images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";

interface FormTextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  type?: string;
  inputStyles?: string;
  containerStyles?: string;
  required?: boolean;
  handleUpdateCartAttributes?: (value: string) => void;
  showErrorIcon?: boolean;
  handleInputChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  icon?: React.ReactElement;
  formatInput?: (value: string) => string;
  showErrorMsg?: boolean;
  autoComplete?: string;
}

export default function FormTextInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  type,
  inputStyles,
  containerStyles,
  handleUpdateCartAttributes,
  required,
  showErrorIcon = true,
  handleInputChange,
  onBlur,
  icon,
  formatInput,
  showErrorMsg = false,
  autoComplete,
  ...props
}: FormTextInputProps<TFieldValues, TName>) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: required }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <div className={cn("space-y-px rounded bg-white", containerStyles)}>
          <div className="relative w-full">
            {label && (
              <label htmlFor={name} className="sr-only">
                {label}
              </label>
            )}
            <input
              {...props}
              name={name}
              type={name === "password" ? "password" : type}
              autoComplete={autoComplete}
              value={value || ""}
              onChange={(e) => {
                const value = e.target.value;
                const formattedValue = formatInput ? formatInput(value) : value;
                onChange(formattedValue);
                handleInputChange?.(formattedValue);
              }}
              className={cn(
                "inputText peer h-input relative z-10 border-0 pt-5 text-base ring-1 focus:z-20 focus:ring-2 focus:ring-black focus:ring-inset sm:text-sm",
                error ? "shadow-error" : "ring-gray-300",
                inputStyles,
              )}
              onBlur={(e) => {
                setIsFocused(false);
                if (handleUpdateCartAttributes && value) {
                  handleUpdateCartAttributes(value);
                }
                onBlur?.(e);
              }}
              ref={(input) => {
                if (input && isFocused) {
                  input.focus();
                }
              }}
            />
            <span
              className={cn(
                "absolute top-1/2 left-3 z-30 -translate-y-1/2 transform text-sm transition-all",
                "peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-gray-500",
                "peer-focus:top-1/4 peer-focus:text-xs",
                "max-w-[calc(100%-2rem)] truncate",
                error?.message && showErrorMsg
                  ? "text-red-400"
                  : "text-gray-400 peer-focus:text-gray-700",
                (value || isFocused || type === "date") && "top-1/4 text-xs",
              )}
              onClick={() => setIsFocused(true)}
            >
              {error?.message && showErrorMsg ? error?.message : label}
            </span>
            {error && showErrorIcon ? (
              <FluidImage
                src={`${FLUID_CDN_URL}/images/checkout/icons/circle-exclamation.svg`}
                alt="error"
                width={14}
                height={14}
                className="red-icons absolute top-1/2 right-3 z-20 -translate-y-1/2 transform"
              />
            ) : icon ? (
              icon
            ) : null}
          </div>
        </div>
      )}
    />
  );
}
